import React, { useState, useMemo } from 'react';
import { Activity, Users, Clock, ArrowLeft, MessageCircle, Search, X, Tag, Hash } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNostrQuery, useNostrProfile, getCachedProfile } from '../hooks/useNostrQuery';
import { useAppContext } from '../hooks/useAppContext';
import { isSpamContent, isSpamAccount } from '../lib/bchFilter';
import { isDeletedEvent } from '../components/PostCard';
import PostCard from '../components/PostCard';
import { timeAgo } from '../lib/utils';
import {
  COMMUNITY_DEFINITION_KIND,
  COMMUNITY_POST_KIND,
  parseCommunity,
  type Community,
} from '../lib/communities';
import { ACTIVITY_LABEL, LABEL_NAMESPACE } from './CommunityViewPage';

// ── Activity Post with community badge ──────────────────────────

function ActivityPost({ event, community }: { event: any; community?: Community }) {
  return (
    <div>
      {/* Community badge — only for community posts (kind 1111) */}
      {community && (
        <Link
          to={`/community/${encodeURIComponent(community.id)}`}
          className="flex items-center gap-2 px-4 pt-3 pb-1 text-[11px] text-muted-foreground hover:text-primary transition-colors group"
        >
          {community.image ? (
            <img src={community.image} alt="" className="w-4 h-4 rounded object-cover" />
          ) : (
            <div className="w-4 h-4 rounded bg-primary/10 flex items-center justify-center">
              <Users className="w-2.5 h-2.5 text-primary/60" />
            </div>
          )}
          <span className="font-medium group-hover:underline">{community.name}</span>
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[9px] font-bold">
            <Tag className="w-2 h-2" /> ACTIVITY
          </span>
        </Link>
      )}
      {/* For kind 1 posts (externally labeled), show a small activity badge */}
      {!community && event.kind === 1 && (
        <div className="flex items-center gap-2 px-4 pt-3 pb-1 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[9px] font-bold">
            <Tag className="w-2 h-2" /> ACTIVITY
          </span>
          <span>Labeled by moderator</span>
        </div>
      )}
      <PostCard event={event} />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────

export default function ActivitiesPage() {
  const { isMuted } = useAppContext();
  const [searchQuery, setSearchQuery] = useState('');

  // Step 1: Fetch ALL community definitions so we can resolve community names
  const { events: communityEvents } = useNostrQuery({
    filters: [{ kinds: [COMMUNITY_DEFINITION_KIND], '#t': ['bch-community'], limit: 200 }],
    allRelays: true,
  });

  const communities = useMemo(() => {
    const map = new Map<string, Community>();
    communityEvents.forEach(ev => {
      try {
        const c = parseCommunity(ev);
        if (c && !c.archived) map.set(c.id, c);
      } catch {}
    });
    return map;
  }, [communityEvents]);

  // Step 2a: Fetch self-labeled community posts (kind 1111) with ACTIVITY label
  const { events: selfLabeledEvents, loading: selfLoading } = useNostrQuery({
    filters: [{ kinds: [COMMUNITY_POST_KIND], '#l': [ACTIVITY_LABEL], limit: 200 }],
    allRelays: true,
  });

  // Step 2b: Fetch external label events (kind 1985) with ACTIVITY label
  // These are labels published by operators/mods pointing to kind 1 posts
  const { events: externalLabelEvents, loading: labelsLoading } = useNostrQuery({
    filters: [{ kinds: [1985], '#l': [ACTIVITY_LABEL], limit: 200 }],
    allRelays: true,
  });

  // Extract event IDs from external labels
  const externallyLabeledIds = useMemo(() => {
    const map = new Map<string, { labelEvent: any; targetId: string }>();
    externalLabelEvents.forEach(ev => {
      // Verify correct namespace
      const hasLabel = ev.tags?.some((t: string[]) =>
        t[0] === 'l' && t[1] === ACTIVITY_LABEL && t[2] === LABEL_NAMESPACE
      );
      if (!hasLabel) return;
      // Find the target event ID from 'e' tags
      const eTag = ev.tags?.find((t: string[]) => t[0] === 'e');
      if (eTag?.[1]) {
        map.set(eTag[1], { labelEvent: ev, targetId: eTag[1] });
      }
    });
    return map;
  }, [externalLabelEvents]);

  // Step 3: Fetch the actual kind 1 posts that were externally labeled
  const externalIds = useMemo(() => Array.from(externallyLabeledIds.keys()), [externallyLabeledIds]);
  const { events: externalPosts, loading: postsLoading } = useNostrQuery({
    filters: externalIds.length > 0 ? [{ ids: externalIds, limit: 200 }] : [],
    enabled: externalIds.length > 0,
    allRelays: true,
  });

  const loading = selfLoading || labelsLoading || postsLoading;

  // Combine self-labeled + externally-labeled posts
  const activities = useMemo(() => {
    const seen = new Set<string>();
    const query = searchQuery.trim().toLowerCase();
    const combined: any[] = [];

    // Process self-labeled community posts
    selfLabeledEvents.forEach(ev => {
      if (seen.has(ev.id)) return;
      // Verify correct namespace
      const hasLabel = ev.tags?.some((t: string[]) =>
        t[0] === 'l' && t[1] === ACTIVITY_LABEL && t[2] === LABEL_NAMESPACE
      );
      if (!hasLabel) return;
      seen.add(ev.id);
      combined.push(ev);
    });

    // Process externally labeled posts (kind 1, etc.)
    externalPosts.forEach(ev => {
      if (seen.has(ev.id)) return;
      seen.add(ev.id);
      combined.push(ev);
    });

    return combined
      .filter(ev => {
        if (isDeletedEvent(ev.id)) return false;
        if (isMuted(ev.pubkey)) return false;
        if (isSpamContent(ev.content)) return false;
        if (isSpamAccount(ev)) return false;

        // Search filter
        if (query) {
          const content = ev.content.toLowerCase();
          if (content.includes(query)) return true;
          const profile = getCachedProfile(ev.pubkey);
          if (profile?.name?.toLowerCase().includes(query)) return true;
          if (profile?.display_name?.toLowerCase().includes(query)) return true;
          // Check community name (for community posts)
          const aTag = ev.tags?.find((t: string[]) => t[0] === 'a' || t[0] === 'A')?.[1];
          if (aTag) {
            const comm = communities.get(aTag);
            if (comm?.name.toLowerCase().includes(query)) return true;
          }
          return false;
        }

        return true;
      })
      .sort((a, b) => b.created_at - a.created_at);
  }, [selfLabeledEvents, externalPosts, isMuted, searchQuery, communities]);

  // Resolve which community each post belongs to
  const getPostCommunity = (ev: any): Community | undefined => {
    const aTag = ev.tags?.find((t: string[]) => t[0] === 'a' || t[0] === 'A')?.[1];
    if (aTag) return communities.get(aTag);
    return undefined;
  };

  // Unique community count
  const uniqueCommunities = useMemo(() => {
    const set = new Set<string>();
    activities.forEach(ev => {
      const c = getPostCommunity(ev);
      if (c) set.add(c.id);
    });
    return set.size;
  }, [activities, communities]);

  // Count externally labeled posts
  const externalCount = activities.filter(ev => externallyLabeledIds.has(ev.id)).length;

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="px-4 py-3">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" /> Activities
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Community activities across all spaces
          </p>
        </div>
      </div>

      <div className="max-w-lg mx-auto">
        {/* Search */}
        <div className="px-4 pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search activities, creators, communities..."
              className="w-full pl-9 pr-8 py-2.5 rounded-xl bg-muted/60 border border-border text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-muted-foreground/10"
              >
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        {!loading && activities.length > 0 && (
          <div className="px-4 pt-3 flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1">
              <Tag className="w-3 h-3 text-primary" />
              {activities.length} activit{activities.length !== 1 ? 'ies' : 'y'}
            </span>
            {uniqueCommunities > 0 && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {uniqueCommunities} communit{uniqueCommunities !== 1 ? 'ies' : 'y'}
              </span>
            )}
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="text-primary hover:underline ml-auto">Clear search</button>
            )}
          </div>
        )}

        {/* How it works — shown when no activities exist yet */}
        {!loading && activities.length === 0 && !searchQuery && (
          <div className="px-4 py-4">
            <div className="glass-card text-center py-8 space-y-3">
              <Activity className="w-12 h-12 mx-auto text-muted-foreground/20" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">No activities yet</p>
                <p className="text-xs text-muted-foreground/70 mt-1 max-w-xs mx-auto leading-relaxed">
                  Activities are posts tagged with the <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-bold"><Tag className="w-2 h-2" /> ACTIVITY</span> label.
                  Post in any <Link to="/communities" className="text-primary hover:underline">Community Space</Link> and use the label button, or moderators can label existing posts from the feed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Search no results */}
        {!loading && activities.length === 0 && searchQuery && (
          <div className="px-4 py-4">
            <div className="glass-card text-center py-8">
              <Search className="w-10 h-10 mx-auto text-muted-foreground/20 mb-2" />
              <p className="text-sm text-muted-foreground">No activities matching "{searchQuery}"</p>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && activities.length === 0 && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        )}

        {/* Activity feed */}
        <div className="divide-y divide-border mt-2">
          {activities.map(ev => (
            <ActivityPost key={ev.id} event={ev} community={getPostCommunity(ev)} />
          ))}
        </div>
      </div>
    </div>
  );
}

